import { Card, Form, Offcanvas } from "react-bootstrap";
import CustomButton from "../../components/CustomButton/CustomButton";
import { useEffect, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { DashSquareFill, PlusSquareFill } from "react-bootstrap-icons";
import ReactQuill from "react-quill-new";
import WebService from "../../utility/WebService";
import { toast } from "react-toastify";
import { quillImageHandler } from "../../utility/quilImageHandler";
import CommonQuill from "../../components/editor/CommonQuill";
import HelperService from "../../utility/HelperService";

interface AddAwardProps {
  show: boolean;
  handleClose: () => void;
  data?: any;
}

type FormData = {
  title: string;
  slug: string;
  createdBy: string;
  meta_description: string;
  keywords: string;
};

const AddAward = ({ show, handleClose, data }: AddAwardProps) => {
  const {
    register,
    handleSubmit,
    trigger,
    formState: { errors },
    reset,
    watch,
  } = useForm<FormData>();
  const [isLoading, setLoading] = useState(false);
  const [logo, setLogo] = useState<File | null>(null);
  const [description, setDescription] = useState("");
  const [categories, setCategories] = useState([{ name: "", file: "" }]);
  const [pastWinners, setPastWinners] = useState([{ name: "", file: "" }]);
  const [videos, setVideos] = useState([{ url: "" }]);
  const [nextAwardDate, setNextAwardDate] = useState<string | null>(null);
  const [fileError, setFileError] = useState<string>("");
  const quillRef = useRef<any>(null);
  const [customErrors, setCustomErrors] = useState<{
    description?: string;
  }>({});
  useEffect(() => {
    const storedUser = localStorage.getItem("UserData");
    const userData = storedUser ? JSON.parse(storedUser) : null;
    if (data) {
      reset({
        title: data.title,
        slug: data.slug,
        keywords: data?.keywords,
        createdBy: userData?.fullName || "",
        meta_description: data?.meta_description,
      });
      setLogo(null);
      setDescription(data.description);
      setNextAwardDate(data.next_award_date || null);
      setCategories(
        data.categories?.length > 0
          ? data.categories
          : [{ name: "", file: null }],
      );
      setPastWinners(
        data.past_winners?.length > 0
          ? data.past_winners
          : [{ name: "", file: null }],
      );
      setVideos(
        data.videos?.length > 0
          ? data.videos.map((url: string) => ({ url }))
          : [{ url: "" }],
      );
    }
  }, [data, show]);
  const isValidYouTubeUrl = (url: string) => {
    const regex =
      /^(https?:\/\/)?(www\.)?(youtube\.com\/watch\?v=|youtu\.be\/)([a-zA-Z0-9_-]{11})/;
    return regex.test(url);
  };

  const getYouTubeEmbedUrl = (url: string) => {
    const match = url.match(
      /(?:youtube\.com\/watch\?v=|youtu\.be\/)([a-zA-Z0-9_-]{11})/,
    );
    return match ? `https://www.youtube.com/embed/${match[1]}` : "";
  };
  const isQuillEmpty = (value?: string) => {
    if (!value) return true;

    // remove html tags, &nbsp; and spaces
    const text = value
      .replace(/<(.|\n)*?>/g, "")
      .replace(/&nbsp;/g, "")
      .trim();

    return text.length === 0;
  };
  const validateInfoTab = async () => {
    const valid = await trigger(["title"]);

    const newErrors: any = {};

    if (isQuillEmpty(description)) {
      newErrors.description = "Description is required";
    }
       setCustomErrors(newErrors);

    return true;
  };
  const ALLOWED_IMAGE_TYPES = [
    "image/jpeg",
    "image/jpg",
    "image/png",
    "image/svg+xml",
  ];
  const onSubmit = async (request: FormData) => {
    if (fileError) {
      toast.error(fileError);
      return;
    }

    if (!data) {
      const valid = await validateInfoTab();
      if (!valid) {
        toast.error("Please fill required fields");
        return;
      }
    }
    setLoading(true);
    const formData = new FormData();

    const storedUser = localStorage.getItem("UserData");
    const userData = storedUser ? JSON.parse(storedUser) : null;
    formData.append("title", request.title);
    formData.append("slug", request.slug);
    formData.append("meta_description", request.meta_description);
    formData.append("keywords", request.keywords);
    if (nextAwardDate) {
      formData.append("next_award_date", nextAwardDate);
    }

    if (data?._id) {
      formData.append("id", data._id);
    }

    if (logo) {
      formData.append("logo", logo);
    }
    if (!data?._id && userData?._id) {
      formData.append("createdBy", userData._id);
    }

    const editor = quillRef.current?.getEditor();
    const html = editor?.root.innerHTML || "";
    formData.append("description", html);
    if (
      categories.length > 0 &&
      categories.every(
        (item) => item.name && item.file && Object.keys(item).length !== 0,
      )
    ) {
      formData.append("categories", JSON.stringify(categories));
    }
    if (
      pastWinners.length > 0 &&
      pastWinners.every(
        (item) => item.name && item.file && Object.keys(item).length !== 0,
      )
    ) {
      formData.append("past_winners", JSON.stringify(pastWinners));
    }
    if (
      videos.length > 0 &&
      videos.every((v) => v.url && isValidYouTubeUrl(v.url))
    ) {
      const videoUrls = videos.map((v) => v.url);

      formData.append("videos", JSON.stringify(videoUrls));
    }

    WebService.fileUploadAPI({
      action: `award/addAward`,
      body: formData,
    })
      .then((res: any) => {
        toast.success(res.message);
        setLoading(false);
        handleClose();
      })
      .catch((err: any) => {
        setLoading(false);
        toast.error(err.response?.data?.message || "An error occurred");
      });
  };

  const uploadFile = (file: File, index: number, type: string) => {
    const formData = new FormData();
    formData.append("file", file);
    formData.append("storage", "AWARDS");
    WebService.fileUploadAPI({
      action: "upload",
      body: formData,
    })
      .then((res: any) => {
        if (!res?.data?.url) return;

        const fileUrl = res.data.url;

        switch (type) {
          case "CATEGORIES": {
            const updated = [...categories];
            updated[index] = {
              ...updated[index],
              file: fileUrl,
            };
            setCategories(updated);
            break;
          }

          case "PAST_WINNERS": {
            const updated = [...pastWinners];
            updated[index] = {
              ...updated[index],
              file: fileUrl,
            };
            setPastWinners(updated);
            break;
          }

          default:
            console.warn("Unknown upload type:", type);
        }

        toast.success("File uploaded successfully");
      })
      .catch((err: any) => {
        toast.error(err.response?.data?.message || "File upload failed");
      });
  };
  const imageHandler = () => {
    // alert("g");
    const input = document.createElement("input");
    input.type = "file";
    input.accept = "image/*";
    input.click();

    input.onchange = async () => {
      const file = input.files?.[0];
      console.log("fileeeee", file);
      if (!file) return;

      const formData = new FormData();
      formData.append("file", file);
      formData.append("storage", "ABOUT_US");

      try {
        const res: any = await WebService.fileUploadAPI({
          action: "upload",
          body: formData,
        });

        const imageUrl = res?.data?.url; // ✅ correct access

        if (!imageUrl) {
          toast.error("Image upload failed");
          return;
        }

        if (!quillRef.current) return;
        const editor = quillRef.current.getEditor();
        const range = editor.getSelection(true);

        editor.insertEmbed(range.index, "image", imageUrl);
        editor.setSelection(range.index + 1);
      } catch (err) {
        toast.error("Image upload failed");
      }
    };
  };
  return (
    <>
      <Offcanvas
        show={show}
        onHide={() => {
          handleClose();
        }}
        placement="end"
        className="offcanvas-custom"
      >
        <Offcanvas.Header closeButton>
          <Offcanvas.Title>{data ? "Edit" : "Add"}</Offcanvas.Title>
        </Offcanvas.Header>
        <Offcanvas.Body>
          <Form>
            <Card className="p-3 bg-light my-3 position-relative">
              <span
                className="bg-white position-absolute rounded small"
                style={{ top: "-12px", left: "12px", padding: "0 8px" }}
              >
                Main Content
              </span>

              <Form.Group className="mb-2" controlId="title">
                <Form.Label className="label">Title</Form.Label>
                <Form.Control
                  type="text"
                  placeholder="Enter title"
                  {...register("title", { required: "Title is required" })}
                />
                {errors.title && (
                  <div className="error-message">{errors.title.message}</div>
                )}
              </Form.Group>
              <Form.Group className="mb-2" controlId="slugField">
                <Form.Label className="label">Slug</Form.Label>{" "}
                <span className="text-danger">*</span>
                <Form.Control
                  type="text"
                  placeholder="Enter slug"
                  {...register("slug")}
                />
              </Form.Group>

              <Form.Group className="mb-2" controlId="mainPageContent">
                <Form.Label className="label">Description</Form.Label>
                {/* <ReactQuill
                  ref={quillRef}
                  theme="snow"
                  value={description}
                  onChange={setDescription}
                  className="editor"
                  modules={{
                    toolbar: {
                      container: [
                        ["bold", "italic", "underline"],
                        [{ list: "ordered" }, { list: "bullet" }],
                        [{ indent: "-1" }, { indent: "+1" }],
                        [{ size: ["small", false, "large"] }],
                        [{ color: [] }, { background: [] }],
                        ["link", "image"],
                        ["emoji"],
                        ["clean"],
                      ],
                      handlers: {
                        image: () =>
                          quillImageHandler({
                            quillRef,
                            storage: "AWARDS",
                          }),
                      },
                    },
                  }}
                /> */}

                <CommonQuill
                  ref={quillRef}
                  value={description}
                  onChange={setDescription}
                  storage="AWARDS"
                  initialHtml={show && data?.description ? HelperService.normalizeResizeHtml(data.description) : undefined}
                  onClearError={() =>
                    setCustomErrors((p) => ({
                      ...p,
                      description: undefined,
                    }))
                  }
                />
                  {customErrors.description && (
                      <div className="error-message">
                        {customErrors.description}
                      </div>
                    )}
              </Form.Group>
              <Form.Group className="mb-2">
                <Form.Label className="label">Next Award Date</Form.Label>

                <Form.Control
                  type="date"
                  value={nextAwardDate || ""}
                  onChange={(e) => setNextAwardDate(e.target.value || null)}
                />

                {nextAwardDate && (
                  <small className="text-muted">
                    Selected: {nextAwardDate}
                  </small>
                )}
              </Form.Group>
              <Form.Group className="mb-2" controlId="createdBy">
                <Form.Label className="label">Created By</Form.Label>
                <Form.Control
                  type="text"
                  placeholder="Created by user"
                  {...register("createdBy")}
                  value={watch("createdBy") || ""}
                  disabled
                />
              </Form.Group>
            </Card>
            <Card className="p-3 bg-light my-3 position-relative">
              <span
                className="bg-white position-absolute rounded small"
                style={{ top: "-12px", left: "12px", padding: "0 8px" }}
              >
                Categories
              </span>

              <Form.Group className="mb-3">
                <Form.Label className="label">Categories</Form.Label>

                <p className="text-primary mt-1" style={{ fontSize: 13 }}>
                  Note: Please upload <strong>PDF / DOC</strong> files only
                </p>

                {categories.map((item, index) => (
                  <div
                    key={index}
                    draggable
                    onDragStart={(e) =>
                      e.dataTransfer.setData("categoryIndex", index.toString())
                    }
                    onDragOver={(e) => e.preventDefault()}
                    onDrop={(e) => {
                      const fromIndex = Number(
                        e.dataTransfer.getData("categoryIndex"),
                      );
                      if (fromIndex === index) return;

                      const updated = [...categories];
                      const [moved] = updated.splice(fromIndex, 1);
                      updated.splice(index, 0, moved);
                      setCategories(updated);
                    }}
                    style={{ cursor: "move" }}
                  >
                    <div className="d-flex align-items-start mb-3">
                      <span className="drag-icon me-2">⋮⋮</span>

                      <div className="flex-1 w-100">
                        {/* FILE UPLOAD */}
                        <Form.Control
                          type="file"
                          accept=".pdf,.doc,.docx"
                          onChange={(e) => {
                            const input = e.target as HTMLInputElement;
                            const file = input.files?.[0];
                            if (file) {
                              const ext = file.name.split(".").pop();
                              const safeFileName = `category_${Date.now()}.${ext}`;

                              const renamedFile = new File(
                                [file],
                                safeFileName,
                                {
                                  type: file.type,
                                },
                              );

                              uploadFile(renamedFile, index, "CATEGORIES");
                            }
                          }}
                        />

                        {/* FILE PREVIEW */}
                        {item.file && (
                          <div className="mt-2">
                            <a
                              href={item.file}
                              target="_blank"
                              rel="noreferrer"
                              className="text-primary"
                            >
                              📄 View Uploaded File
                            </a>
                          </div>
                        )}

                        {/* NAME */}
                        <Form.Control
                          className="mt-2"
                          placeholder="Category name"
                          value={item.name}
                          onChange={(e) => {
                            const updated = [...categories];
                            updated[index].name = e.target.value;
                            setCategories(updated);
                          }}
                        />

                        {/* ACTIONS */}
                        <div className="d-flex gap-2 mt-2">
                          <PlusSquareFill
                            size={24}
                            className="cursor-pointer"
                            onClick={() =>
                              setCategories([
                                ...categories,
                                { name: "", file: "" },
                              ])
                            }
                          />

                          {categories.length > 1 && (
                            <DashSquareFill
                              size={24}
                              className="cursor-pointer"
                              onClick={() =>
                                setCategories(
                                  categories.filter((_, i) => i !== index),
                                )
                              }
                            />
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </Form.Group>
            </Card>

            <Card className="p-3 bg-light my-3 position-relative">
              <span
                className="bg-white position-absolute rounded small"
                style={{ top: "-12px", left: "12px", padding: "0 8px" }}
              >
                Past Winners
              </span>

              <Form.Group className="mb-3">
                <Form.Label className="label"> Past Winners</Form.Label>

                <p className="text-primary mt-1" style={{ fontSize: 13 }}>
                  Note: Please upload <strong>PDF / DOC</strong> files only
                </p>

                {pastWinners.map((item, index) => (
                  <div
                    key={index}
                    draggable
                    onDragStart={(e) =>
                      e.dataTransfer.setData("winnerIndex", index.toString())
                    }
                    onDragOver={(e) => e.preventDefault()}
                    onDrop={(e) => {
                      const fromIndex = Number(
                        e.dataTransfer.getData("winnerIndex"),
                      );
                      if (fromIndex === index) return;

                      const updated = [...pastWinners];
                      const [moved] = updated.splice(fromIndex, 1);
                      updated.splice(index, 0, moved);
                      setPastWinners(updated);
                    }}
                    style={{ cursor: "move" }}
                  >
                    <div className="d-flex align-items-start mb-3">
                      <span className="drag-icon me-2">⋮⋮</span>

                      <div className="flex-1 w-100">
                        {/* FILE UPLOAD */}
                        <Form.Control
                          type="file"
                          accept=".pdf,.doc,.docx"
                          onChange={(e) => {
                            const input = e.target as HTMLInputElement;
                            const file = input.files?.[0];
                            if (file) {
                              const ext = file.name.split(".").pop();
                              const safeFileName = `winner_${Date.now()}.${ext}`;

                              const renamedFile = new File(
                                [file],
                                safeFileName,
                                {
                                  type: file.type,
                                },
                              );

                              uploadFile(renamedFile, index, "PAST_WINNERS");
                            }
                          }}
                        />

                        {/* FILE PREVIEW */}
                        {item.file && (
                          <div className="mt-2">
                            <a
                              href={item.file}
                              target="_blank"
                              rel="noreferrer"
                              className="text-primary"
                            >
                              📄 View Uploaded File
                            </a>
                          </div>
                        )}

                        {/* NAME */}
                        <Form.Control
                          className="mt-2"
                          placeholder="Winners name"
                          value={item.name}
                          onChange={(e) => {
                            const updated = [...pastWinners];
                            updated[index].name = e.target.value;
                            setPastWinners(updated);
                          }}
                        />

                        {/* ACTIONS */}
                        <div className="d-flex gap-2 mt-2">
                          <PlusSquareFill
                            size={24}
                            className="cursor-pointer"
                            onClick={() =>
                              setPastWinners([
                                ...pastWinners,
                                { name: "", file: "" },
                              ])
                            }
                          />

                          {pastWinners.length > 1 && (
                            <DashSquareFill
                              size={24}
                              className="cursor-pointer"
                              onClick={() =>
                                setCategories(
                                  pastWinners.filter((_, i) => i !== index),
                                )
                              }
                            />
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </Form.Group>
            </Card>

            <Card className="p-3 bg-light my-3 position-relative">
              <span
                className="bg-white position-absolute rounded small"
                style={{ top: "-12px", left: "12px", padding: "0 8px" }}
              >
                Media
              </span>
              <Form.Group className="mb-2" controlId="logo">
                <p
                  className="text-primary mt-1"
                  style={{ fontSize: "13px" }}
                >
                  Note: Please upload an image of size:{" "}
                  <strong>237 × 74 px</strong> in{" "}
                  <strong>.png, .jpeg, .jpg or .svg</strong> format.
                </p>
                <Form.Label className="label">Logo</Form.Label>
                <Form.Control
                  type="file"
                  accept="image/*"
                  className="custom-image-upload"
                  onChange={(e) => {
                    const input = e.target as HTMLInputElement;
                    const selectedFile = input.files?.[0];

                    if (!selectedFile) return;

                    if (!ALLOWED_IMAGE_TYPES.includes(selectedFile.type)) {
                      setFileError(
                        "Image format not supported. Please upload PNG, JPG, JPEG or SVG. image",
                      );
                      // console.log(fileError)
                      //      toast.error(fileError);
                      setLogo(null);
                      input.value = ""; // reset input
                      return;
                    }

                    // ✅ valid
                    setFileError("");
                    setLogo(selectedFile);
                  }}
                />
                {fileError && (
                  <p className="text-danger mt-1" style={{ fontSize: "13px" }}>
                    {fileError}
                  </p>
                )}
              </Form.Group>
              {(logo || data?.logo) && (
                <div className="mb-2 event-image">
                  <img
                    src={logo ? URL.createObjectURL(logo) : data?.logo}
                    alt="Event"
                    style={{
                      maxWidth: "100%",
                      maxHeight: 200,
                      objectFit: "contain",
                    }}
                  />
                </div>
              )}
            </Card>
            <Card className="p-3 bg-light my-3 position-relative">
              <span
                className="bg-white position-absolute rounded small"
                style={{ top: "-12px", left: "12px", padding: "0 8px" }}
              >
                Videos
              </span>

              <Form.Group className="mb-3">
                <Form.Label className="label">Videos</Form.Label>

                {videos.map((item, index) => {
                  const isValid = isValidYouTubeUrl(item.url);
                  const embedUrl = getYouTubeEmbedUrl(item.url);

                  return (
                    <div key={index} className="mb-4">
                      {/* URL INPUT */}
                      <Form.Control
                        placeholder="Paste YouTube URL"
                        value={item.url}
                        onChange={(e) => {
                          const updated = [...videos];
                          updated[index].url = e.target.value;
                          setVideos(updated);
                        }}
                      />

                      {/* ERROR */}
                      {item.url && !isValid && (
                        <div
                          className="text-danger mt-1"
                          style={{ fontSize: 13 }}
                        >
                          Please enter a valid YouTube URL
                        </div>
                      )}

                      {/* PREVIEW */}
                      {isValid && embedUrl && (
                        <div className="mt-2">
                          <iframe
                            width="100%"
                            height="200"
                            src={embedUrl}
                            title="YouTube preview"
                            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                            allowFullScreen
                            style={{ borderRadius: 8 }}
                          />
                        </div>
                      )}

                      {/* ACTIONS */}
                      <div className="d-flex mt-2 gap-2">
                        <PlusSquareFill
                          size={26}
                          className="cursor-pointer"
                          onClick={() => setVideos([...videos, { url: "" }])}
                        />

                        {videos.length > 1 && (
                          <DashSquareFill
                            size={26}
                            className="cursor-pointer"
                            onClick={() =>
                              setVideos(videos.filter((_, i) => i !== index))
                            }
                          />
                        )}
                      </div>
                    </div>
                  );
                })}
              </Form.Group>
            </Card>

            <Card className="p-3 bg-light my-3 position-relative">
              <span
                className="bg-white position-absolute rounded small"
                style={{ top: "-12px", left: "12px", padding: "0 8px" }}
              >
                SEO
              </span>
              <Form.Label className="label">
                Meta Description
                <span className="text-danger">*</span>
              </Form.Label>
              <Form.Control
                as="textarea"
                placeholder="Meta Description"
                {...register("meta_description", {
                  required: "Meta description is required",
                })}
              />
              {errors.meta_description && (
                <div className="error-message">
                  {errors.meta_description.message}
                </div>
              )}
              <Form.Group className="mb-2" controlId="eventName">
                <Form.Label className="label">
                  Keywords <span className="text-danger">*</span>
                </Form.Label>
                <Form.Control
                  type="text"
                  placeholder="Enter keywords"
                  {...register("keywords", {
                    required: "Keywords are required",
                  })}
                />
                {errors.keywords && (
                  <div className="error-message">{errors.keywords.message}</div>
                )}
              </Form.Group>
            </Card>
          </Form>
        </Offcanvas.Body>
        <div className="d-flex justify-content-end p-3">
          <CustomButton
            isLoading={isLoading}
            label="Save"
            className="w-100"
            onClick={handleSubmit(onSubmit)}
          />
        </div>
      </Offcanvas>
    </>
  );
};

export default AddAward;
